import { Button, Form, Layout, DatePicker, Card, Divider, Radio } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../../components/Loading/Loading";
import { fetchClient, updateClient } from "../Clients/utils/fetch";
import {
  CustomerInformation,
  PriceInfo,
  ProductsInformation,
  PartitionsInformation,
  PaymentFormat,
  ContractPriceIncrease,
  PaymentMethod,
} from "./components";
import {
  ProForm,
  PageContainer,
  FooterToolbar,
} from "@ant-design/pro-components";
import {
  fetchLead,
  saveSpecification as requestSaveSpecification,
  getBodyToUpdateClient,
} from "./utils/fetch";
import { useForm } from "antd/es/form/Form";
import { getTotalPriceWithDiscount } from "./utils/price";
import { banks } from "./utils/banks";
import { getInitialValues } from "./utils/values";
import { changePageTitle } from "../../utils/title";

const NewLead = ({ setToken }) => {
  const params = useParams();
  const clientId = params.clientId;
  const leadId = params.leadId;

  const [form] = useForm();
  const navigate = useNavigate();

  const [customerInfo, setCustomerInfo] = useState({
    clientType: "INDIVIDUAL",
    address: "",
  });
  const [client, setClient] = useState();
  const [isLoadingClient, setIsLoadingClient] = useState(true);
  const [specification, setSpecification] = useState([]);
  const [lead, setLead] = useState();
  const [isLoadingLead, setIsLoadingLead] = useState(true);

  const [clientType, setClientType] = useState();
  const [discount, setDiscount] = useState(0);
  const [increasePrice, setIncreasePrice] = useState(0);
  const [productCategory, setProductCategory] = useState("DOORS");

  const handleCategoryChange = (e) => {
    const next = e.target.value;
    if (next === productCategory) return;
    if (specification.length > 0) {
      const ok = window.confirm(
        "При смене категории список товаров будет очищен. Продолжить?"
      );
      if (!ok) return;
      setSpecification([]);
    }
    setProductCategory(next);
  };

  const navigateToClientPage = () => {
    navigate(`/client/${clientId}`);
  };

  const saveSpecification = (values) => {
    let companyEntity = form.getFieldValue("companyEntity");
    if (clientType === "ENTITY") {
      companyEntity = "INTERCORP_HALYK";
    }
    const mergedValues = { ...values, companyEntity };
    const clientUpdateBody = getBodyToUpdateClient(mergedValues);
    delete clientUpdateBody.companyEntity;
    updateClient(clientId, clientUpdateBody);
    requestSaveSpecification(getBody(mergedValues), navigateToClientPage);
  };

  useEffect(() => {
    changePageTitle("Новая спецификация");
  }, []);

  useEffect(() => {
    if (clientId) {
      fetchClient(clientId, setClient, setIsLoadingClient);
    } else {
      setIsLoadingClient(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (leadId) {
      fetchLead(leadId, setLead, setIsLoadingLead);
    }
  }, [leadId]);

  useEffect(() => {
    setClientType(client?.clientType || "INDIVIDUAL");
  }, [client]);

  useEffect(() => {
    calculatePrepayment();
  }, [specification]);

  const getBody = (values) => {
    // Форматируем заказы, устанавливая явный null для удаленных аксессуаров
    const formattedOrders = specification.map((product) => {
      const formattedProduct = { ...product, description: product?.description || product?.comment || "" };

      if (!formattedProduct.productId && formattedProduct.product) {
        formattedProduct.productId = formattedProduct.product.productId;
      }

      // Явно устанавливаем null для удаленных аксессуаров (бэкенд требует явный null для удаления)
      if (!formattedProduct.doorKnob || formattedProduct.doorKnob === null || formattedProduct.doorKnob === undefined) {
        formattedProduct.doorKnob = null;
      }
      if (!formattedProduct.doorPanel || formattedProduct.doorPanel === null || formattedProduct.doorPanel === undefined) {
        formattedProduct.doorPanel = null;
      }
      if (!formattedProduct.doorstep || formattedProduct.doorstep === null || formattedProduct.doorstep === undefined) {
        formattedProduct.doorstep = null;
      }
      if (!formattedProduct.latch || formattedProduct.latch === null || formattedProduct.latch === undefined) {
        formattedProduct.latch = null;
      }

      return formattedProduct;
    });

    return {
      lead: leadId,
      paymentMethod: values.paymentMethod || "",
      paymentFormat: {
        prepayment: values.prepayment,
        remains: values.remains,
      },
      contractPrice: {
        percentage: values.constractPriceIncreasePercentage || 0,
        note: values.constractPriceIncreaseDescription || "",
      },
      deadline: values.deadline,
      discount: values.discount || 0,
      designerDiscount: values.designerDiscount || 0,
      orders: formattedOrders,
      companyEntity: values.companyEntity,
    };
  };

  const calculatePrepayment = () => {
    const totalPrice = getTotalPriceWithDiscount(
      specification || [],
      form.getFieldValue("constractPriceIncreasePercentage"),
      form.getFieldValue("discount")
    );

    const prepayment = parseInt(totalPrice * 0.7);

    form.setFieldValue("prepayment", prepayment);

    calculateRemains();
  };

  const calculateRemains = () => {
    const discount = form.getFieldValue("discount") || 0;

    const totalPrice = getTotalPriceWithDiscount(
      specification || [],
      form.getFieldValue("constractPriceIncreasePercentage"),
      discount
    );

    form.setFieldValue(
      "remains",
      parseInt(
        totalPrice -
        (totalPrice * discount) / 100 -
        form.getFieldValue("prepayment")
      )
    );
  };

  const onValuesChange = (value) => {
    if (value.clientType) {
      setClientType(value.clientType);
      if (value.clientType === "ENTITY") {
        form.setFieldValue("companyEntity", "INTERCORP_HALYK");
      } else if (value.clientType === "ENTITY_NO_VAT") {
        form.setFieldValue("companyEntity", "IP_KEPLER_BCC");
      } else {
        form.setFieldValue("companyEntity", "IP_KEPLER_HALYK");
      }
    }
    if (value.discount || value.discount === 0) {
      setDiscount(value.discount);
    }

    if (value.prepayment) {
      calculateRemains();
    }

    if (
      value.constractPriceIncreasePercentage ||
      value.constractPriceIncreasePercentage === 0
    ) {
      setIncreasePrice(value.constractPriceIncreasePercentage);
    }

    if (value.accountNumber) {
      const accountNumber = value.accountNumber;
      if (accountNumber.length >= 7) {
        const bankCode = accountNumber.substring(4, 7);

        const bank = banks.find((v) => v.code === bankCode);

        if (bank) {
          form.setFieldValue("bankName", bank.name);
          form.setFieldValue("BIC", bank.BIC);
        } else {
          form.setFieldValue("bankName", "");
          form.setFieldValue("BIC", "");
        }
      } else {
        form.setFieldValue("bankName", "");
        form.setFieldValue("BIC", "");
      }
    }

    if (
      value.discount ||
      value.discount === 0 ||
      value.constractPriceIncreasePercentage ||
      value.constractPriceIncreasePercentage === 0
    ) {
      calculatePrepayment();
    }
  };

  if (isLoadingClient || !client || isLoadingLead || !lead) {
    return <Loading />;
  }

  return (
    <Layout>
      <PageContainer title="Новая заявка">
        <Card>
          <ProForm
            form={form}
            onValuesChange={(values) => {
              onValuesChange(values);
            }}
            onReset={() => {
              setClientType("INDIVIDUAL");
              setDiscount(0);
              setSpecification([]);
            }}
            submitter={{
              render: (_, _dom) => (
                <FooterToolbar>
                  <Button type="primary" htmlType="submit">
                    Сохранить
                  </Button>
                </FooterToolbar>
              ),
            }}
            onFinish={(values) => {
              saveSpecification(values);
            }}
            initialValues={getInitialValues(
              client,
              discount,
              increasePrice,
              lead
            )}
          >
            <CustomerInformation
              setToken={setToken}
              customerInfo={customerInfo}
              setCustomerInfo={setCustomerInfo}
              client={client}
              clientType={clientType}
              disabled={true}
              requisitesDisabled={false}
            />
            <Divider />
            <div style={{ marginBottom: 16 }}>
              <Radio.Group
                value={productCategory}
                onChange={handleCategoryChange}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="DOORS">Двери</Radio.Button>
                <Radio.Button value="PARTITIONS">
                  Алюминиевые перегородки
                </Radio.Button>
              </Radio.Group>
            </div>
            {productCategory === "DOORS" ? (
              <ProductsInformation
                setToken={setToken}
                selectedProducts={specification}
                setSelectedProducts={setSpecification}
                onChange={calculatePrepayment}
                increasePrice={form.getFieldValue(
                  "constractPriceIncreasePercentage"
                )}
              />
            ) : (
              <PartitionsInformation
                setToken={setToken}
                selectedProducts={specification}
                setSelectedProducts={setSpecification}
                clientType={clientType}
                onChange={calculatePrepayment}
              />
            )}
            <PriceInfo
              selectedProducts={specification}
              discount={discount}
              increasePrice={increasePrice}
              clientType={clientType || "INDIVIDUAL"}
            />
            <Divider
              style={{
                marginTop: 0,
              }}
            />
            <div
              style={{
                display: "flex",
              }}
            >
              <div
                style={{
                  marginRight: 40,
                }}
              >
                <PaymentFormat />
              </div>
              <div
                style={{
                  marginRight: 40,
                }}
              >
                <PaymentMethod />
              </div>
            </div>
            <Divider />
          </ProForm>
        </Card>
      </PageContainer>
    </Layout>
  );
};

export default NewLead;
